/**
 * フライトログ（ulog）ページ
 * - SD カードから取り出した .ulg を機体に紐づけて S3 へ保管する（手動アップロード）
 * - 保存済みログの一覧・日付絞り込み・ダウンロード（生の .ulg をそのまま返す）
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { deviceApi, flightLogApi } from '../services/api';
import { userApi } from '../services/userApi';
import { naturalSort } from '../services/utils';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Select, Badge } from '../components/ui';
import FlightLogTable, { formatSize, formatDateTime as formatDate } from '../components/FlightLogTable';

const UPLOAD_ROLES = ['superadmin', 'admin', 'operator'];

const STAGE_LABELS = {
  queued: { label: '待機', variant: 'default' },
  hashing: { label: 'ハッシュ計算', variant: 'info' },
  requesting: { label: 'URL取得', variant: 'info' },
  uploading: { label: 'アップロード中', variant: 'info' },
  verifying: { label: '検証中', variant: 'info' },
  done: { label: '保存済み', variant: 'success' },
  error: { label: 'エラー', variant: 'danger' },
};

const DATE_SOURCE_LABELS = {
  gps: 'GPS時刻',
  filename: 'ファイル名',
  client: '申告値',
  upload: 'アップロード時刻',
};

export default function FlightLogs() {
  const [profile, setProfile] = useState(null);
  const [things, setThings] = useState([]);
  const [thingName, setThingName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // アップロードキュー: { id, file, stage, percent, result, error }
  const [queue, setQueue] = useState([]);
  const [running, setRunning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [listRefresh, setListRefresh] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    userApi.me().then(setProfile).catch(() => {});
  }, []);

  const fetchThings = useCallback(async () => {
    setLoading(true);
    try {
      // GET /devices は { devices, nextToken } を返す（50 件ずつ）。全ページを集める
      const names = [];
      let token = null;
      do {
        const params = { maxResults: '50' };
        if (token) params.nextToken = token;
        const data = await deviceApi.list(params);
        (data.devices || data.things || []).forEach((d) => { if (d.thingName) names.push(d.thingName); });
        token = data.nextToken || null;
      } while (token);
      const list = [...new Set(names)].sort(naturalSort);
      setThings(list);
      if (list.length > 0) setThingName((cur) => cur || list[0]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThings();
  }, [fetchThings]);

  const canUpload = profile && UPLOAD_ROLES.includes(profile.role);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const accepted = files.filter((f) => /\.ulg$/i.test(f.name));
    const rejected = files.length - accepted.length;
    if (rejected > 0) setError(`${rejected} 件は .ulg ではないため除外しました`);
    setQueue((q) => [
      ...q,
      ...accepted.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file, stage: 'queued', percent: 0, result: null, error: null,
      })),
    ]);
  };

  const updateItem = (id, patch) => setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const runQueue = async () => {
    if (!thingName) { setError('機体を選択してください'); return; }
    setRunning(true);
    setError(null);
    const pending = queue.filter((it) => it.stage === 'queued' || it.stage === 'error');
    for (const item of pending) {
      try {
        const result = await flightLogApi.uploadFile(thingName, item.file, (stage, percent) => {
          updateItem(item.id, { stage, percent: percent ?? 0, error: null });
        });
        updateItem(item.id, { stage: 'done', percent: 100, result });
      } catch (err) {
        updateItem(item.id, { stage: 'error', error: err.message });
      }
    }
    setRunning(false);
    setListRefresh((n) => n + 1);
  };

  const removeItem = (id) => setQueue((q) => q.filter((it) => it.id !== id));
  const clearDone = () => setQueue((q) => q.filter((it) => it.stage !== 'done'));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canUpload) return;
    addFiles(e.dataTransfer.files);
  };

  const pendingCount = queue.filter((it) => it.stage === 'queued' || it.stage === 'error').length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">
        フライトログ（PX4 ulog）を機体ごとに S3 へ保管します。SD カードから取り出した .ulg をそのまま投入してください。
      </p>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      {profile && !canUpload && (
        <Alert variant="warning">閲覧者ロールではアップロードできません。ダウンロードのみ利用できます。</Alert>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* 投入 */}
        <div className="lg:col-span-1 space-y-4">
          <Card title="アップロード">
            <div className="space-y-4">
              <Select
                label="機体"
                required
                value={thingName}
                onChange={(e) => setThingName(e.target.value)}
                disabled={loading || running}
                options={things.map((t) => ({ value: t, label: t }))}
                placeholder={loading ? '読み込み中...' : '機体を選択'}
              />

              {canUpload && (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`
                    border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
                    ${dragOver ? 'border-primary-500 bg-primary-50' : 'border-slate-300 hover:border-primary-400 hover:bg-gray-50'}
                  `}
                >
                  <Icon name="uploadCloud" className="w-8 h-8 mx-auto text-slate-400" />
                  <p className="mt-2 text-sm text-slate-600">.ulg をドロップ、またはクリックして選択</p>
                  <p className="text-xs text-slate-400 mt-1">複数ファイル可。同じ内容のログは登録済みとして拒否されます</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".ulg"
                    multiple
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                  />
                </div>
              )}

              {canUpload && (
                <div className="flex gap-2">
                  <Button
                    onClick={runQueue}
                    disabled={pendingCount === 0 || !thingName}
                    loading={running}
                    className="flex-1"
                  >
                    <Icon name="upload" className="w-4 h-4" />
                    {pendingCount > 0 ? `${pendingCount} 件をアップロード` : 'アップロード'}
                  </Button>
                  <Button variant="secondary" onClick={clearDone} disabled={running || queue.every((it) => it.stage !== 'done')}>
                    完了分を消す
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card title="開始時刻の決め方" padding="p-4">
            <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
              <li>ログ内の GPS 時刻（UTC）を最優先で使います</li>
              <li>GPS が無い場合は SD カードのファイル名（YYYY-MM-DD/HH_MM_SS.ulg）から推定します</li>
              <li>どちらも無ければアップロード時刻になります</li>
            </ul>
          </Card>
        </div>

        {/* キュー */}
        <div className="lg:col-span-2">
          <Card title={`アップロード状況${queue.length ? `（${queue.length}）` : ''}`} padding="p-0">
            {queue.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Icon name="inbox" className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                ファイルが追加されていません
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {queue.map((it) => {
                  const st = STAGE_LABELS[it.stage] || STAGE_LABELS.queued;
                  return (
                    <div key={it.id} className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Icon name="file" className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 truncate">{it.file.name}</span>
                            <Badge variant={st.variant} size="sm">{st.label}</Badge>
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatSize(it.file.size)}
                            {it.result && (
                              <>
                                {' · 開始 '}{formatDate(it.result.logStartUtc)}
                                {' （'}{DATE_SOURCE_LABELS[it.result.dateSource] || it.result.dateSource}{'）'}
                                {it.result.ulogInfo?.ver_sw && ` · PX4 ${it.result.ulogInfo.ver_sw}`}
                              </>
                            )}
                            {it.error && <span className="text-red-600"> · {it.error}</span>}
                          </div>
                          {it.stage === 'uploading' && (
                            <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-primary-500 transition-all" style={{ width: `${it.percent}%` }} />
                            </div>
                          )}
                        </div>
                        {(it.stage === 'queued' || it.stage === 'error' || it.stage === 'done') && !running && (
                          <button
                            className="p-1.5 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            onClick={() => removeItem(it.id)}
                            title="一覧から外す"
                          >
                            <Icon name="x" className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* 保存済みログ */}
      <Card title="保存済みフライトログ" padding="p-4">
        <p className="text-xs text-slate-500 mb-3">
          ダウンロードした .ulg は Flight Review、PlotJuggler、pyulog でそのまま開けます。
        </p>
        <FlightLogTable refreshKey={listRefresh} onError={setError} />
      </Card>
    </div>
  );
}
